import { yupResolver } from '@hookform/resolvers/yup';
import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate } from 'react-router-dom';
import * as yup from 'yup';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import TextInput from '~components/FormFields/v2/TextInput';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { UNPROCESSABLE_ENTITY } from '~constants/errorMessagesConsts';
import { FeatureFlags } from '~constants/featureFlags';
import { FastWeighConnection, FastWeighConnectionTest } from '~hooks/useFastWeigh/models';
import useFastWeigh, { FastWeighFlowKey } from '~hooks/useFastWeigh/useFastWeigh';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import { ROUTES as routes } from '~router/routePaths';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { useAwaitableFeatureFlag } from '~utils/hooks/useFeatureFlag';

const apiKeySchema = yup.object().shape({
  apiKey: yup
    .string()
    .trim()
    .required(
      t('form_validation_errors.required', {
        field: t('administration.integration.fast_weigh.api_key_label'),
      }),
    ),
});

type ApiKeyFormDto = yup.InferType<typeof apiKeySchema>;

// The 422 body the API returns for a rejected key. Declared here rather than imported
// from the spec package: UI components read the model layer, not transport types.
type ApiFieldErrorResponse = {
  error: { errors?: Array<{ field: string; message: string }> };
};

// The three flows the connection can push or pull independently. `updateKey` doubles
// as the PATCH body key, so a toggle assembles its payload from this entry alone.
const FLOWS = [
  {
    updateKey: 'project_push_enabled',
    labelKey: 'administration.integration.fast_weigh.flow.project_push',
    descriptionKey: 'administration.integration.fast_weigh.flow.project_push_description',
    readValue: (connection: FastWeighConnection) => connection.projectPushEnabled,
  },
  {
    updateKey: 'order_push_enabled',
    labelKey: 'administration.integration.fast_weigh.flow.order_push',
    descriptionKey: 'administration.integration.fast_weigh.flow.order_push_description',
    readValue: (connection: FastWeighConnection) => connection.orderPushEnabled,
  },
  {
    updateKey: 'ticket_pull_enabled',
    labelKey: 'administration.integration.fast_weigh.flow.ticket_pull',
    descriptionKey: 'administration.integration.fast_weigh.flow.ticket_pull_description',
    readValue: (connection: FastWeighConnection) => connection.ticketPullEnabled,
  },
] as const satisfies ReadonlyArray<{
  updateKey: FastWeighFlowKey;
  labelKey: string;
  descriptionKey: string;
  readValue: (connection: FastWeighConnection) => boolean;
}>;

// Fast-Weigh only reports the write flag for an accepted key, so a null stays silent
// rather than claiming the key is read-only.
function describeWriteAccess(writePermission: boolean | null) {
  if (writePermission === true) {
    return t('administration.integration.fast_weigh.write_access_granted');
  }

  if (writePermission === false) {
    return t('administration.integration.fast_weigh.write_access_read_only');
  }

  return '';
}

const FastWeighManagement = observer(() => {
  const { userStore } = useStores();
  const featureFlag = useAwaitableFeatureFlag({
    featureFlagKey: FeatureFlags.fastWeighEnabled,
  });
  const canManage = userStore.getPermissions().canManageFastWeighIntegration;

  if (!featureFlag.isReady) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!featureFlag.isFeatureFlagEnabled || !canManage) {
    return <Navigate to={routes.base} replace />;
  }

  return <FastWeighManagementPage />;
});

const FastWeighManagementPage = observer(() => {
  const { toasterStore, userStore } = useStores();
  const {
    createConnection,
    deleteConnection,
    listConnections,
    testConnection,
    updateConnection,
  } = useFastWeigh();

  const canDelete = userStore.getPermissions().canDeleteFastWeighIntegration;

  const [isLoading, setIsLoading] = useState(true);
  const [connection, setConnection] = useState<FastWeighConnection | null>(null);
  const [isEditingKey, setIsEditingKey] = useState(false);
  const [isSavingKey, setIsSavingKey] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<FastWeighConnectionTest | null>(null);
  const [savingFlowKey, setSavingFlowKey] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [didLoadFail, setDidLoadFail] = useState(false);

  const { control, handleSubmit, reset, setError } = useForm<ApiKeyFormDto>({
    resolver: yupResolver(apiKeySchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: { apiKey: '' },
  });

  useEffect(() => {
    const loadConnection = async () => {
      setIsLoading(true);
      try {
        const connections = await listConnections();
        // `null` is a failed fetch, `[]` is a company that has never connected.
        // Collapsing them would offer Connect to a company that already has a
        // connection, and a second create is rejected by the API.
        setDidLoadFail(connections === null);
        setConnection(connections?.[0] ?? null);
      } finally {
        setIsLoading(false);
      }
    };

    void loadConnection();
  }, [listConnections]);

  const applyKeyRejection = useCallback(
    (error: unknown) => {
      const isRejectedByServer =
        isAxiosError<ApiFieldErrorResponse>(error) &&
        error.response?.status === UNPROCESSABLE_ENTITY;

      // The hook only re-throws a 422, so anything else here is unexpected. Re-throwing
      // rather than painting it onto the key field lets it reach the global handler
      // instead of being reported to the user as a bad credential.
      if (!isRejectedByServer) throw error;

      const firstError = error.response?.data.error.errors?.[0];
      setError('apiKey', {
        message: firstError?.message ?? t('error_messages.fast_weigh.key_rejected'),
      });
    },
    [setError],
  );

  const handleKeySubmit = handleSubmit(async ({ apiKey }) => {
    setIsSavingKey(true);
    try {
      // A brand-new connection turns the master switch on; the three flow toggles all
      // default off, so connecting alone still neither pushes nor pulls.
      const saved = connection
        ? await updateConnection(connection.id, { api_key: apiKey })
        : await createConnection({ api_key: apiKey, enabled: true });

      if (!saved) return;

      setConnection(saved);
      setTestResult(null);
      setIsEditingKey(false);
      reset({ apiKey: '' });
      toasterStore.push(
        alert(
          t('administration.integration.fast_weigh.toast.key_saved'),
          AlertTypes.success,
        ),
      );
    } catch (error) {
      applyKeyRejection(error);
    } finally {
      setIsSavingKey(false);
    }
  });

  const handleTestConnection = useCallback(async () => {
    if (!connection) return;

    setIsTesting(true);
    try {
      const result = await testConnection(connection.id);
      if (!result) return;

      setTestResult(result);
      setConnection(result.connection);
    } finally {
      setIsTesting(false);
    }
  }, [connection, testConnection]);

  const handleFlowToggle = useCallback(
    async (updateKey: FastWeighFlowKey, isEnabled: boolean) => {
      if (!connection) return;

      setSavingFlowKey(updateKey);
      try {
        const updated = await updateConnection(connection.id, {
          [updateKey]: isEnabled,
        });
        if (!updated) return;

        setConnection(updated);
      } finally {
        setSavingFlowKey(null);
      }
    },
    [connection, updateConnection],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!connection) return;

    setIsDeleting(true);
    try {
      const deleted = await deleteConnection(connection.id);
      if (!deleted) return;

      setConnection(null);
      setTestResult(null);
      setIsEditingKey(false);
      setIsDeleteDialogOpen(false);
      reset({ apiKey: '' });
      toasterStore.push(
        alert(
          t('administration.integration.fast_weigh.toast.disconnected'),
          AlertTypes.success,
        ),
      );
    } finally {
      setIsDeleting(false);
    }
    // toasterStore is a stable MobX singleton — deliberately omitted, since this
    // codebase's convention is that a MobX store must never sit in a dependency array.
  }, [connection, deleteConnection, reset]);

  // Any in-flight mutation disables every other action: each one resolves into the
  // same `connection` state, so a slower response would otherwise clobber a newer one.
  const isBusy = isSavingKey || isTesting || isDeleting || savingFlowKey !== null;
  const isKeyFormVisible = !connection || isEditingKey;

  const writeAccessMessage = describeWriteAccess(testResult?.writePermission ?? null);

  return (
    <Box sx={{ maxWidth: '800px' }}>
      <Button
        color="secondary"
        component={Link}
        startIcon={<ArrowBackIosNew fontSize="small" />}
        to={`/${routes.settings.base}/${routes.settings.integrations.base}`}
        variant="text"
      >
        {t('administration.integration.title')}
      </Button>
      <SettingsPageHeader
        title={t('administration.integration.fast_weigh.name')}
        description={t('administration.integration.fast_weigh.description')}
      />

      {didLoadFail ? (
        <Alert severity="error" data-test-id="fast-weigh-load-error">
          {t('error_messages.fast_weigh.failed_to_fetch_connections')}
        </Alert>
      ) : isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 3 }} data-test-id="fast-weigh-key-section">
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t('administration.integration.fast_weigh.api_key_title')}
            </Typography>

            {isKeyFormVisible ? (
              <Box
                component="form"
                onSubmit={handleKeySubmit}
                sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
              >
                <TextInput
                  control={control}
                  dataUi="fast-weigh-api-key"
                  // Masked while typing: this is a long-lived, write-capable
                  // credential for the customer's own ERP.
                  slotProps={{ htmlInput: { type: 'password' } }}
                  label={t('administration.integration.fast_weigh.api_key_label')}
                  name="apiKey"
                  placeholder={t(
                    'administration.integration.fast_weigh.api_key_placeholder',
                  )}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {connection ? (
                    <SmallButton
                      color="brandV2Black"
                      disabled={isBusy}
                      onClick={() => {
                        setIsEditingKey(false);
                        reset({ apiKey: '' });
                      }}
                      variant="outlined"
                      sx={{ borderColor: 'divider', color: 'text.primary' }}
                    >
                      {t('actions.cancel')}
                    </SmallButton>
                  ) : null}
                  <SmallLoadingButton
                    color="brandV2Yellow"
                    disabled={isBusy && !isSavingKey}
                    loading={isSavingKey}
                    type="submit"
                  >
                    {connection
                      ? t('administration.integration.fast_weigh.cta.replace_key')
                      : t('administration.integration.fast_weigh.cta.connect')}
                  </SmallLoadingButton>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography sx={{ fontFamily: 'monospace' }}>
                  {t('administration.integration.fast_weigh.api_key_masked', {
                    lastFour: connection?.apiKeyLastFour ?? '',
                  })}
                </Typography>
                <SmallButton
                  color="brandV2Black"
                  disabled={isBusy}
                  onClick={() => setIsEditingKey(true)}
                  variant="outlined"
                  sx={{ borderColor: 'divider', color: 'text.primary' }}
                >
                  {t('administration.integration.fast_weigh.cta.replace_key')}
                </SmallButton>
              </Box>
            )}
          </Box>

          {connection ? (
            <>
              <Divider sx={{ mb: 3 }} />

              <Box sx={{ mb: 3 }} data-test-id="fast-weigh-test-section">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <SmallLoadingButton
                    color="brandV2Black"
                    disabled={isBusy && !isTesting}
                    loading={isTesting}
                    onClick={handleTestConnection}
                    variant="outlined"
                    sx={{ borderColor: 'divider', color: 'text.primary' }}
                  >
                    {t('administration.integration.fast_weigh.cta.test_connection')}
                  </SmallLoadingButton>
                  {connection.externalTenantId ? (
                    <Chip
                      data-test-id="fast-weigh-tenant-chip"
                      label={t('administration.integration.fast_weigh.tenant_chip', {
                        tenant: connection.externalTenantId,
                      })}
                      size="small"
                    />
                  ) : null}
                </Box>

                {testResult ? (
                  <Alert
                    severity={connection.connected ? 'success' : 'error'}
                    data-test-id="fast-weigh-test-result"
                  >
                    {connection.connected
                      ? [
                          connection.externalTenantId
                            ? t('administration.integration.fast_weigh.test_success', {
                                tenant: connection.externalTenantId,
                              })
                            : t(
                                'administration.integration.fast_weigh.test_success_no_tenant',
                              ),
                          writeAccessMessage,
                        ]
                          .filter(Boolean)
                          .join(' ')
                      : (connection.lastErrorMessage ??
                        t('error_messages.fast_weigh.key_rejected'))}
                  </Alert>
                ) : null}
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Box sx={{ mb: 3 }} data-test-id="fast-weigh-flows-section">
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {t('administration.integration.fast_weigh.flows_title')}
                </Typography>
                {FLOWS.map((flow) => (
                  <Box key={flow.updateKey} sx={{ mb: 1.5 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={flow.readValue(connection)}
                          data-test-id={`fast-weigh-toggle-${flow.updateKey}`}
                          disabled={isBusy}
                          onChange={(event) =>
                            void handleFlowToggle(flow.updateKey, event.target.checked)
                          }
                        />
                      }
                      label={t(flow.labelKey)}
                    />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t(flow.descriptionKey)}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {canDelete ? (
                <>
                  <Divider sx={{ mb: 3 }} />
                  <SmallButton
                    color="brandV2Black"
                    disabled={isBusy}
                    onClick={() => setIsDeleteDialogOpen(true)}
                    variant="outlined"
                    sx={{ borderColor: 'divider', color: 'text.primary' }}
                  >
                    {t('administration.integration.fast_weigh.cta.disconnect')}
                  </SmallButton>
                </>
              ) : null}
            </>
          ) : null}
        </>
      )}

      <ConfirmationDialog
        confirmButtonProps={{
          children: t('administration.integration.fast_weigh.cta.disconnect'),
        }}
        isBusy={isDeleting}
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t('administration.integration.fast_weigh.disconnect_confirmation_title')}
      >
        <Typography>
          {t('administration.integration.fast_weigh.disconnect_confirmation_body')}
        </Typography>
      </ConfirmationDialog>
    </Box>
  );
});

export default FastWeighManagement;
